export interface CVData {
  name: string;
  jobTitle: string;
  basicInfo: string[];
  profile: string;
  keySkills: string[];
  workExperience: WorkEntry[];
  education: EducationEntry[];
  interests: string[];
  references: string;
  sectionNames?: {
    basicInfo: string;
    profile: string;
    keySkills: string;
    workExperience: string;
    education: string;
    interests: string;
    references: string;
  };
}

export interface WorkEntry {
  jobTitle: string;
  dates: string;
  company: string;
  bullets: string[];
}

export interface EducationEntry {
  qualification: string;
  dates: string;
  institution: string;
  notes: string[];
}

// Ordered section keywords to detect headings in the raw AI text
const SECTION_KEYS: Record<string, string> = {
  "basic information": "basicInfo",
  "basic info": "basicInfo",
  "contact": "basicInfo",
  "profile": "profile",
  "personal statement": "profile",
  "professional summary": "profile",
  "summary": "profile",
  "key skills": "keySkills",
  "skills": "keySkills",
  "core skills": "keySkills",
  "work experience": "workExperience",
  "experience": "workExperience",
  "professional experience": "workExperience",
  "employment": "workExperience",
  "education": "education",
  "qualifications": "education",
  "interests": "interests",
  "hobbies": "interests",
  "hobbies and interests": "interests",
  "references": "references",
};

const DATE_PATTERN = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)[\s\-]?\d{4}\b|\d{4}\s*[-–]\s*(\d{4}|present|current|now)/i;

function detectSectionKey(line: string): string | null {
  const clean = line.trim().toLowerCase().replace(/[:\-–]/g, "").trim();
  if (!clean || clean.length < 3) return null; // Avoid matching empty lines or very short fragments
  
  for (const key of Object.keys(SECTION_KEYS)) {
    // 1. Exact match
    if (clean === key) return SECTION_KEYS[key];
    
    // 2. Starts with key followed by non-alphanumeric (e.g., "Work Experience -")
    if (clean.startsWith(key)) {
      const remaining = clean.slice(key.length).trim();
      
      // SPECIAL CASE: "References available upon request" is content, not a header
      if (key === "references" && remaining.toLowerCase().includes("available")) {
        continue;
      }

      // If nothing remains or only symbols remain, it's a header
      if (!remaining || /^[^a-z0-9]/.test(remaining)) {
        return SECTION_KEYS[key];
      }
    }
  }
  return null;
}

function isDateLine(text: string): boolean {
  return DATE_PATTERN.test(text);
}

// Month names we recognise (3-letter or full)
const MONTH_RX = /JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|JANUARY|FEBRUARY|MARCH|APRIL|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER/i;

function extractInlineDates(line: string): { text: string; dates: string } | null {
  // 1. Primary Strategy: split on 2+ whitespace
  const parts = line.split(/\s{2,}/);
  if (parts.length >= 2) {
    const datePart = parts[parts.length - 1].trim();
    const textPart = parts.slice(0, parts.length - 1).join("  ").trim();

    // Date must start with a recognised month OR a 4-digit year
    const startsWithMonth = MONTH_RX.test(datePart.split(/[\s\-–]/)[0]);
    const startsWithYear  = /^\d{4}/.test(datePart);

    if ((startsWithMonth || startsWithYear) && textPart.length > 0) {
      return { text: textPart, dates: datePart };
    }
  }

  // 2. Fallback: Use the DATE_PATTERN to find dates at the end of a line even with single space
  // This matches things like "Software Engineer Jun 2021 - Present"
  const match = line.match(new RegExp(`(.*)\\s(${DATE_PATTERN.source})$`, 'i'));
  if (match) {
    const textPart = match[1].trim();
    const datePart = match[2].trim();
    if (textPart && datePart) {
      return { text: textPart, dates: datePart };
    }
  }

  return null;
}

function splitRawSections(raw: string): { sections: Record<string, string[]>; sectionNames: Record<string, string> } {
  const lines = raw.split(/\r?\n/);
  const sections: Record<string, string[]> = {
    header: [],
    basicInfo: [],
    profile: [],
    keySkills: [],
    workExperience: [],
    education: [],
    interests: [],
    references: [],
  };
  const sectionNames: Record<string, string> = {
    basicInfo: "Basic Information",
    profile: "Profile",
    keySkills: "Key Skills",
    workExperience: "Work Experience",
    education: "Education",
    interests: "Interests",
    references: "References"
  };

  let currentSection = "header";

  for (const line of lines) {
    const sectionKey = detectSectionKey(line);
    if (sectionKey) {
      currentSection = sectionKey;
      const lowerClean = line.trim().toLowerCase();
      if (sectionKey === "references" && lowerClean.includes("available")) {
        sectionNames[sectionKey] = "References";
      } else {
        sectionNames[sectionKey] = line.trim();
      }
      continue;
    }
    sections[currentSection].push(line);
  }

  return { sections, sectionNames };
}

function parseHeader(lines: string[]): { name: string; jobTitle: string } {
  const nonEmpty = lines.filter(l => l.trim());
  const name = nonEmpty[0] || "";
  const jobTitle = nonEmpty[1] || "";
  return { name: name.trim(), jobTitle: jobTitle.trim() };
}

function parseWorkExperience(lines: string[]): WorkEntry[] {
  const entries: WorkEntry[] = [];
  let current: WorkEntry | null = null;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    // Detect "Job Title         DATE - DATE" pattern
    const inlineDateMatch = extractInlineDates(line);
    if (inlineDateMatch) {
      if (current) entries.push(current);
      current = {
        jobTitle: inlineDateMatch.text,
        dates: inlineDateMatch.dates,
        company: "",
        bullets: [],
      };
      continue;
    }

    // If the line starts with bullet point markers
    if (line.startsWith("•") || line.startsWith("-") || line.startsWith("*")) {
      if (current) {
        current.bullets.push(line.replace(/^[•\-\*]\s*/, "").trim());
      }
      continue;
    }

    // If we have a current entry and no company yet, this is the company line
    if (current && !current.company) {
      current.company = line;
      continue;
    }

    // Otherwise treat as a new job title line if it's bold-like (short, no leading spaces)
    if (line.length < 80 && !isDateLine(line)) {
      if (current) entries.push(current);
      current = { jobTitle: line, dates: "", company: "", bullets: [] };
    }
  }

  if (current) entries.push(current);
  return entries;
}

function parseEducation(lines: string[]): EducationEntry[] {
  const entries: EducationEntry[] = [];
  let current: EducationEntry | null = null;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const inlineDateMatch = extractInlineDates(line);
    if (inlineDateMatch) {
      if (current) entries.push(current);
      current = {
        qualification: inlineDateMatch.text,
        dates: inlineDateMatch.dates,
        institution: "",
        notes: [],
      };
      continue;
    }

    if (line.startsWith("•") || line.startsWith("-")) {
      if (current) current.notes.push(line.replace(/^[•\-]\s*/, "").trim());
      continue;
    }

    if (current && !current.institution) {
      current.institution = line;
      continue;
    }

    if (line.startsWith("(") || (current && current.institution)) {
      if (current) current.notes.push(line);
      continue;
    }

    if (current) entries.push(current);
    current = { qualification: line, dates: "", institution: "", notes: [] };
  }

  if (current) entries.push(current);
  return entries;
}

export function parseCV(raw: string): CVData {
  try {
    const trimmed = raw.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") {
        const normalized: any = {};
        for (const [k, v] of Object.entries(parsed)) {
          const lowerK = k.toLowerCase();
          if (lowerK === "sectionnames" && v && typeof v === "object") {
            const normalizedSecNames: any = {};
            for (const [sk, sv] of Object.entries(v)) {
              normalizedSecNames[sk.toLowerCase()] = sv;
            }
            normalized.sectionNames = normalizedSecNames;
          } else {
            normalized[lowerK] = v;
          }
        }

        const resolveKey = (key: string, backup: string) => {
          return normalized[key] !== undefined ? normalized[key] : normalized[backup];
        };

        const secNames = normalized.sectionnames || {};
        return {
          name: normalized.name || "",
          jobTitle: resolveKey("jobtitle", "jobTitle") || "",
          basicInfo: Array.isArray(resolveKey("basicinfo", "basicInfo")) ? resolveKey("basicinfo", "basicInfo") : [],
          profile: normalized.profile || "",
          keySkills: Array.isArray(resolveKey("keyskills", "keySkills")) ? resolveKey("keyskills", "keySkills") : [],
          workExperience: Array.isArray(resolveKey("workexperience", "workExperience")) ? resolveKey("workexperience", "workExperience") : [],
          education: Array.isArray(normalized.education) ? normalized.education : [],
          interests: Array.isArray(normalized.interests) ? normalized.interests : [],
          references: normalized.references || "",
          sectionNames: {
            basicInfo: secNames.basicinfo || secNames.basicInfo || "Basic Information",
            profile: secNames.profile || "Profile",
            keySkills: secNames.keyskills || secNames.keySkills || "Key Skills",
            workExperience: secNames.workexperience || secNames.workExperience || "Work Experience",
            education: secNames.education || "Education",
            interests: secNames.interests || "Interests",
            references: secNames.references || "References"
          }
        };
      }
    }
  } catch (e) {
    console.warn("parseCV: Failed to parse raw string as JSON, falling back to text parsing", e);
  }

  const { sections, sectionNames } = splitRawSections(raw);

  const { name, jobTitle } = parseHeader(sections.header);

  const basicInfo = sections.basicInfo
    .flatMap(line => {
      const l = line.trim();
      if (!l) return [];
      
      // Smarter split for combined contact info (e.g. "email@domain.com, Mobile: +123")
      // Only split if it contains an email AND a phone indicator to avoid splitting addresses
      const hasEmail = l.includes('@');
      const hasPhone = /mobile|phone|tel:|\+\d/i.test(l);
      
      if (hasEmail && hasPhone) {
        const parts = l.split(/[,;|]/);
        if (parts.length > 1) {
          return parts.map(p => p.trim()).filter(Boolean);
        }
      }
      return [l];
    })
    .filter(Boolean);

  const profile = sections.profile
    .map(l => l.trim())
    .filter(Boolean)
    .join(" ");

  let keySkills = sections.keySkills
    .map(l => l.trim().replace(/^[•\-\*]\s*/, ""))
    .filter(Boolean);

  const workExperience = parseWorkExperience(sections.workExperience);
  const education = parseEducation(sections.education);

  let interests = sections.interests
    .map(l => l.trim().replace(/^[•\-\*]\s*/, ""))
    .filter(Boolean);

  let references = sections.references
    .map(l => l.trim())
    .filter(Boolean)
    .join(" ");

  // --- Smarter References Recovery & Deduplication ---
  const refSentence = "References available upon request.";
  const refSentenceLower = refSentence.toLowerCase();

  // 1. Check if references are accidentally inside interests or skills (common AI mistake)
  if (!references) {
    const interestIdx = interests.findIndex(i => i.toLowerCase().includes("references available"));
    if (interestIdx !== -1) {
      references = refSentence;
      interests.splice(interestIdx, 1);
    } else {
      const skillIdx = keySkills.findIndex(s => s.toLowerCase().includes("references available"));
      if (skillIdx !== -1) {
        references = refSentence;
        keySkills.splice(skillIdx, 1);
      }
    }
  }

  // 2. Remove duplicates of the ref sentence from all lists
  interests = interests.filter(i => !i.toLowerCase().includes("references available"));
  keySkills = keySkills.filter(s => !s.toLowerCase().includes("references available"));

  // 3. Fallback only if we really have nothing
  if (!references) {
      references = refSentence;
  }

  // 4. Final Section Name Cleanup (Deduplicate)
  if (sectionNames.interests === sectionNames.references) {
    sectionNames.interests = "Interests";
  }

  return {
    name,
    jobTitle,
    basicInfo,
    profile,
    keySkills,
    workExperience,
    education,
    interests,
    references,
    sectionNames: sectionNames as any,
  };
}

export function stringifyCV(data: CVData): string {
  console.log('stringifyCV input:', data);
  let lines: string[] = [];

  // Header
  if (data.name) lines.push(data.name);
  if (data.jobTitle) lines.push(data.jobTitle);
  lines.push("");

  // Basic Info
  if (data.basicInfo && data.basicInfo.length > 0) {
    if (data.sectionNames?.basicInfo) lines.push(data.sectionNames.basicInfo);
    data.basicInfo.forEach(info => lines.push(info));
    lines.push("");
  }

  // Profile
  if (data.profile) {
    if (data.sectionNames?.profile) lines.push(data.sectionNames.profile);
    lines.push(data.profile);
    lines.push("");
  }

  // Key Skills
  if (data.keySkills && data.keySkills.length > 0) {
    if (data.sectionNames?.keySkills) lines.push(data.sectionNames.keySkills);
    data.keySkills.forEach(skill => lines.push(`- ${skill}`));
    lines.push("");
  }

  // Work Experience
  if (data.workExperience && data.workExperience.length > 0) {
    if (data.sectionNames?.workExperience) lines.push(data.sectionNames.workExperience);
    data.workExperience.forEach(work => {
      if (work.jobTitle || work.dates) {
        lines.push(`${work.jobTitle || ""}  ${work.dates || ""}`.trim());
      }
      if (work.company) lines.push(work.company);
      if (work.bullets && work.bullets.length > 0) {
        work.bullets.forEach(bullet => lines.push(`- ${bullet}`));
      }
      lines.push("");
    });
  }

  // Education
  if (data.education && data.education.length > 0) {
    if (data.sectionNames?.education) lines.push(data.sectionNames.education);
    data.education.forEach(edu => {
      if (edu.qualification || edu.dates) {
        lines.push(`${edu.qualification || ""}  ${edu.dates || ""}`.trim());
      }
      if (edu.institution) lines.push(edu.institution);
      if (edu.notes && edu.notes.length > 0) {
        edu.notes.forEach(note => {
          if (note.startsWith("(") || note.startsWith("-")) {
            lines.push(note);
          } else {
            lines.push(`- ${note}`);
          }
        });
      }
      lines.push("");
    });
  }

  // Interests
  if (data.interests && data.interests.length > 0) {
    if (data.sectionNames?.interests) lines.push(data.sectionNames.interests);
    data.interests.forEach(interest => lines.push(`- ${interest}`));
    lines.push("");
  }

  // References
  if (data.references) {
    if (data.sectionNames?.references) lines.push(data.sectionNames.references);
    lines.push(data.references);
    lines.push("");
  }

  const result = lines.join("\n").trim();
  console.log('stringifyCV output length:', result.length, 'first 200 chars:', result.substring(0, 200));
  return result;
}
